// Step 18 — AI sidebar navigation.
//
// Two layers of navigation:
//
//   Global — Overview / Deals / Ask Qada / Settings — always visible.
//   Deal-scoped — Buyers / Intelligence / Risks / Obligations /
//     Contracts / Actions / Activity — only when the current URL is
//     inside a specific deal (/deals/:workspaceId/…).
//
// Each nav item is wired to an existing backend capability. Where a
// capability is not yet implemented in the backend (Obligations), the
// route still exists and the page renders a truthful empty state
// rather than fake data — see requirements in Step 18.

import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "../../services/AuthContext";

import { useAIWorkspace } from "./AIWorkspaceContext";

const GLOBAL_NAV = [
  { to: "/", label: "Översikt", end: true, icon: "◐" },
  { to: "/deals", label: "Affärer", end: false, icon: "▤" },
  { to: "/chat", label: "Ask Qada", end: false, icon: "✦" },
  { to: "/settings", label: "Inställningar", end: true, icon: "⚙" },
];

function dealNavItems(workspaceId) {
  const base = `/deals/${workspaceId}`;
  return [
    { to: base, label: "Översikt", end: true },
    { to: `${base}/command`, label: "Kommandocentral", end: true },
    { to: `${base}/buyers`, label: "Köpare", end: false },
    { to: `${base}/intelligence`, label: "Intelligence", end: true },
    { to: `${base}/risks`, label: "Risker", end: true },
    { to: `${base}/obligations`, label: "Åtaganden", end: true },
    { to: `${base}/contracts`, label: "Avtal", end: true },
    { to: `${base}/actions`, label: "Åtgärder", end: true },
    { to: `${base}/execution`, label: "Genomförande", end: true },
    { to: `${base}/activity`, label: "Aktivitet", end: true },
  ];
}

export default function AISidebar() {
  const { user, activeWorkspace, activeWorkspaceId } = useAIWorkspace();
  const { logout } = useAuth();
  const location = useLocation();

  // Only show the deal-scoped nav when the URL is inside a deal
  // (avoid showing it for "orphan" workspace context when the user is
  // on Overview or Deals list).
  const inDealScope = /^\/deals\/\d+/.test(location.pathname);
  const dealItems =
    inDealScope && activeWorkspaceId ? dealNavItems(activeWorkspaceId) : [];

  const displayName = user?.full_name || user?.email || "";

  return (
    <aside className="qai-shell-sidebar">
      <div className="qai-sidebar-brand">
        <span className="qai-brand-mark">Q</span>
        <span>Qada AI</span>
      </div>

      <nav className="qai-shell-nav" aria-label="Global">
        {GLOBAL_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              "qai-shell-nav-item" + (isActive ? " qai-shell-nav-item-active" : "")
            }
          >
            {item.icon && <span className="qai-shell-nav-icon">{item.icon}</span>}
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {dealItems.length > 0 && (
        <>
          <div className="qai-shell-nav-heading">
            {activeWorkspace?.organisation_name || "Affär"}
          </div>
          <nav className="qai-shell-nav qai-shell-nav-scoped" aria-label="Deal">
            {dealItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  "qai-shell-nav-item qai-shell-nav-item-scoped" +
                  (isActive ? " qai-shell-nav-item-active" : "")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      <div className="qai-shell-sidebar-footer">
        {displayName && (
          <div className="qai-sidebar-user">
            <div className="qai-sidebar-user-name">{displayName}</div>
          </div>
        )}
        <button type="button" className="qai-logout" onClick={logout}>
          Logga ut
        </button>
      </div>
    </aside>
  );
}
